"use client"
import { useEffect, useRef, useState } from "react"
import { MapPin, X, Star, Waves, Wind, Navigation2, ChevronRight, Info } from "lucide-react"
import { useSurfSpots } from "@/hooks/use-surf-spots"
import type { SurfSpot, ActivityType } from "@/lib/types/surf-spot"
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription } from "@/components/ui/drawer"
import { Button } from "@/components/ui/button"
import { usePlatform, triggerHaptic } from "@/hooks/use-platform"
import { useRouter } from "next/navigation"
import { Badge } from "@/components/ui/badge"
import { calculateDistance } from "@/lib/utils/geolocation"

import { cn } from "@/lib/utils"

interface MapViewProps {
  activityFilter?: ActivityType | 'all'
}

export function MapView({ activityFilter = 'all' }: MapViewProps) {
  const router = useRouter()
  const { isMobile } = usePlatform()
  const mapRef = useRef<HTMLDivElement>(null)
  const mapInstanceRef = useRef<any>(null)
  const markersRef = useRef<any[]>([])
  const [selectedSpot, setSelectedSpot] = useState<SurfSpot | null>(null)
  const [isMapLoaded, setIsMapLoaded] = useState(false)
  const { spots, loading } = useSurfSpots()

  const [isLegendOpen, setIsLegendOpen] = useState(true)

  // Filter spots based on activity
  const filteredSpots = spots.filter(spot => {
    if (activityFilter === 'all') return true
    
    const spotType = spot.activity_type
    if (!spotType) return true // Default to show if type unknown
    
    if (activityFilter === 'surfing') {
      return spotType === 'surfing' || spotType === 'both'
    }
    if (activityFilter === 'kitesurfing') {
      return spotType === 'kitesurfing' || spotType === 'both'
    }
    return true
  })

  useEffect(() => {
    const loadLeaflet = async () => {
      if (typeof window === "undefined") return

      try {
        // Load Leaflet CSS
        const link = document.createElement("link")
        link.rel = "stylesheet"
        link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"
        document.head.appendChild(link)

        // Load Leaflet JS
        const script = document.createElement("script")
        script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"
        script.onload = () => {
          initializeMap()
        }
        document.head.appendChild(script)
      } catch (error) {
        console.error("Failed to load Leaflet:", error)
      }
    }

    const initializeMap = () => {
      if (!mapRef.current || mapInstanceRef.current) return

      const L = (window as any).L
      if (!L) return

      const map = L.map(mapRef.current, {
        center: [52.3676, 4.5317], // Center on Netherlands for our initial spots
        zoom: 8, // Closer zoom for Netherlands
        zoomControl: true,
        scrollWheelZoom: true,
        doubleClickZoom: true,
        dragging: true,
      })

      L.tileLayer("https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png", {
        attribution: "© OpenStreetMap contributors © CARTO",
        maxZoom: 18,
      }).addTo(map)

      mapInstanceRef.current = map
      setIsMapLoaded(true)
    }

    loadLeaflet()

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove()
        mapInstanceRef.current = null
      }
      markersRef.current = []
    }
  }, [])

  useEffect(() => {
    if (!mapInstanceRef.current || !filteredSpots.length || loading) {
      // Still clear markers if no spots to show
      if (mapInstanceRef.current) {
        markersRef.current.forEach((marker) => marker.remove())
        markersRef.current = []
      }
      return
    }

    const L = (window as any).L
    if (!L) return

    // Clear existing markers
    markersRef.current.forEach((marker) => marker.remove())
    markersRef.current = []

    filteredSpots.forEach((spot) => {
      // Determine marker color and emoji based on activity type
      let markerColor = '#3b82f6' // Default blue
      let emoji = ''
      
      if (spot.activity_type === 'surfing') {
        markerColor = '#3b82f6' // Blue for surf
        emoji = '🏄'
      } else if (spot.activity_type === 'kitesurfing') {
        markerColor = '#14b8a6' // Teal for kite
        emoji = '🪁'
      } else if (spot.activity_type === 'both') {
        markerColor = '#8b5cf6' // Purple for both
        emoji = '🌊'
      }

      const icon = L.divIcon({
        html: `<div style="
          background: ${markerColor};
          color: white;
          border: 2px solid white;
          border-radius: 50%;
          width: 44px;
          height: 44px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          font-size: 10px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.3);
          transition: all 0.2s ease;
        ">
          <span style="font-size: 14px; margin-bottom: -2px;">${emoji}</span>
          <span>${spot.avg_overall_rating > 0 ? spot.avg_overall_rating.toFixed(1) : "N"}</span>
        </div>`,
        className: "custom-surf-marker",
        iconSize: [44, 44],
        iconAnchor: [22, 22],
      })

      const customMarker = L.marker([spot.latitude, spot.longitude], { icon }).addTo(mapInstanceRef.current)

      customMarker.on("click", () => {
        triggerHaptic('impact')
        setSelectedSpot(spot)
      })

      markersRef.current.push(customMarker)
    })
  }, [filteredSpots, loading])

  const renderSpotDetails = () => {
    if (!selectedSpot) return null

    return (
      <div className="flex flex-col gap-6 p-6">
        {/* Spot Header Image & Quick Info */}
        <div className="relative h-48 -mx-6 -mt-6 mb-4 overflow-hidden rounded-t-[2.5rem]">
          {selectedSpot.image_url ? (
            <img 
              src={selectedSpot.image_url} 
              alt={selectedSpot.name} 
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
              <Waves className="w-12 h-12 text-slate-300 dark:text-slate-700" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <div className="absolute bottom-4 left-6 right-6 flex items-end justify-between">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Badge className="bg-blue-600 border-none text-[10px] font-black uppercase tracking-widest px-2 py-0.5 h-auto">
                  {selectedSpot.activity_type === 'both' ? 'Surf & Kite' : selectedSpot.activity_type || 'Surf'}
                </Badge>
                {selectedSpot.avg_overall_rating > 0 && (
                  <div className="flex items-center gap-1 bg-white/20 backdrop-blur-md rounded-lg px-2 py-0.5">
                    <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                    <span className="text-[10px] font-black text-white">{selectedSpot.avg_overall_rating.toFixed(1)}</span>
                  </div>
                )}
              </div>
              <h2 className="text-2xl font-black text-white uppercase tracking-tight leading-tight">
                {selectedSpot.name}
              </h2>
              <div className="flex items-center gap-1 text-white/80">
                <MapPin className="w-3 h-3" />
                <span className="text-[10px] font-bold uppercase tracking-widest">{selectedSpot.location}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Spot Stats Grid */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-slate-50 dark:bg-slate-900/50 p-4 rounded-2xl border border-slate-100 dark:border-slate-800 flex flex-col gap-1">
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Wave Type</span>
            <span className="text-sm font-bold text-slate-900 dark:text-white capitalize text-nowrap truncate">
              {selectedSpot.wave_type?.replace("-", " ") || "Beach Break"}
            </span>
          </div>
          <div className="bg-slate-50 dark:bg-slate-900/50 p-4 rounded-2xl border border-slate-100 dark:border-slate-800 flex flex-col gap-1">
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Skill Level</span>
            <span className="text-sm font-bold text-slate-900 dark:text-white capitalize">
              {selectedSpot.skill_level || "All Levels"}
            </span>
          </div>
        </div>

        {/* Description */}
        {selectedSpot.description && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Info className="w-3 h-3 text-blue-500" />
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">About the spot</span>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-400 italic leading-relaxed line-clamp-3">
              "{selectedSpot.description}"
            </p>
          </div>
        )}

        {/* Action Button */}
        <Button 
          onClick={() => {
            triggerHaptic('selection')
            router.push(`/spot/${selectedSpot.slug}`)
          }}
          className="w-full h-14 rounded-2xl bg-blue-600 hover:bg-blue-700 text-white font-black uppercase tracking-widest text-xs shadow-xl shadow-blue-500/20 active:scale-95 transition-all mt-2 group"
        >
          Explore Spot
          <ChevronRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
        </Button>
      </div>
    )
  }

  return (
    <div className="relative w-full h-full bg-slate-900">
      <div ref={mapRef} className="absolute inset-0 z-0" />

      {/* Loading state */}
      {(!isMapLoaded || loading) && (
        <div className="absolute inset-0 bg-slate-900 flex items-center justify-center z-10">
          <div className="text-white text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-4"></div>
            <p>{loading ? "Loading surf spots..." : "Loading interactive map..."}</p>
          </div>
        </div>
      )}

      {/* Legend - responsive positioning and sizing - adjusted to not be cut off by nav bar */}
      <div 
        className={cn(
          "absolute bottom-[calc(24px+64px+env(safe-area-inset-bottom))] left-6 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm rounded-2xl shadow-xl z-20 border border-slate-100 dark:border-slate-800 transition-all duration-300 md:bottom-10 md:left-10",
          isLegendOpen ? "p-3 sm:p-4 w-[220px]" : "p-3 w-auto"
        )}
      >
        <button 
          onClick={() => {
            triggerHaptic('selection')
            setIsLegendOpen(!isLegendOpen)
          }}
          className="flex items-center gap-2 w-full text-left"
        >
          <div className="w-6 h-6 bg-blue-50 dark:bg-blue-900/20 rounded-lg flex items-center justify-center shrink-0">
            <MapPin className="w-3.5 h-3.5 text-blue-500" />
          </div>
          {isLegendOpen && (
            <h3 className="font-black text-slate-900 dark:text-white text-[10px] sm:text-xs uppercase tracking-widest flex-1">
              Map Legend
            </h3>
          )}
          <ChevronRight 
            className={cn(
              "w-4 h-4 text-slate-400 transition-transform duration-300",
              isLegendOpen ? "rotate-90" : "rotate-0"
            )} 
          />
        </button>

        {isLegendOpen && (
          <div className="mt-4 space-y-3 animate-in fade-in slide-in-from-top-2 duration-300">
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <div className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-[10px] font-bold shadow-sm border border-white">🏄</div>
                <span className="text-[10px] sm:text-xs font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Surf Spots</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="bg-teal-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-[10px] font-bold shadow-sm border border-white">🪁</div>
                <span className="text-[10px] sm:text-xs font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Kite Spots</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="bg-purple-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-[10px] font-bold shadow-sm border border-white">🌊</div>
                <span className="text-[10px] sm:text-xs font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Both Activities</span>
              </div>
            </div>
            <div className="pt-3 border-t border-slate-100 dark:border-slate-800">
              <p className="text-[10px] text-slate-400 font-medium italic leading-tight">Click markers to view details</p>
            </div>
          </div>
        )}
      </div>

      {/* Spot details popup - redesigned with Drawer */}
      <Drawer 
        open={!!selectedSpot} 
        onOpenChange={(open) => !open && setSelectedSpot(null)}
      >
        <DrawerContent className="max-h-[85vh] bg-white dark:bg-slate-900 border-none rounded-t-[3rem] z-[100]">
          <div className="mx-auto w-12 h-1.5 bg-slate-200 dark:bg-slate-800 rounded-full mt-3 mb-2" />
          
          {/* Accessibility requirements */}
          <DrawerHeader className="sr-only">
            <DrawerTitle>{selectedSpot?.name || "Spot Details"}</DrawerTitle>
            <DrawerDescription>
              Details and forecast for {selectedSpot?.name || "the selected surf spot"}
            </DrawerDescription>
          </DrawerHeader>

          <div className="overflow-y-auto pb-10">
            {renderSpotDetails()}
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  )
}
